import React, { useEffect, useRef, useState } from 'react'
import isMobile from 'ismobilejs'
import parse from 'html-react-parser'
import {
  Assets,
  cookie,
  goToRoute,
  isAuthenticated,
  LazyImage,
  localstorage,
  parseTranslations,
  PublishedComponent,
  Routes
} from '@pdrj/pmi-iqos-shared-react'
import {
  BlockingLoader,
  Button,
  Container,
  Css,
  HtmlContent,
  Icon,
  Image,
  ListContainer,
  TextBody,
  TextTitle,
  ToastContext
} from '@pdrj/pmi-iqos-ds-react'
import { getInitialPropsMock } from '@iqosphere-common/utils'
import { api, IqosphereAnalytics } from '@iqosphere-common/services'
import constants from '@iqosphere-common/constants'
import SpinWheelTranslations from './spin-wheel.json'
import Wheel from './wheel'
import WheelPopupOverlayModal from './popup-overlay-modal'
import Countdown from './animated-numbers'
import { SlideButton } from '@iqosphere-common/components'
import useSpinWheel from './spin-wheel-hook'
import WinVideo from './win-video'

const SPIN_COST = 10
const DAILY_SPINS = 5

const CONTENT_TOKENS = {
  Points: '[[points]]',
  PointsCost: '[[pointsCost]]',
  Spins: '[[spins]]',
  SpinsLeft: '[[spinsLeft]]',
  MaxSpins: '[[maxSpins]]',
  PrizeReward: '[[PrizeReward]]'
}

const SpinWheelComponent = ({
  addressConfirmationUrl,
  campaignCode,
  closeRedirectUrl,
  loosingJourneyContents,
  mission,
  missionCTA,
  actionCTA,
  fallbackCTA,
  lossDefaultCTA,
  postLoginContents,
  spinWheelAction,
  freeSpinContents,
  translations,
  missionStatusId
}) => {
  const [ready, setReady] = useState(false)
  const [isSpinEnabled, setIsSpinEnabled] = useState(false)
  const [spinsLeft, setSpinsLeft] = useState()
  const [pointsLeft, setPointsLeft] = useState()
  const [prevPointsLeft, setPrevPointsLeft] = useState(null)
  const [popupContent, setPopupContent] = useState(null)
  const [freeSpinFlow, setFreeSpinFlow] = useState(false)
  const [showWinVideo, setShowWinVideo] = useState(false)
  const [latestSpinResult, setLatestSpinResult] = useState(null)

  const wheelRef = useRef(null)
  const wheelRef2 = useRef(null)
  const pointerRef = useRef(null)

  const { startSpin, stopSpin } = useSpinWheel({
    wheelRef,
    wheelRef2,
    pointerRef
  })
  const { popupToast } = ToastContext.getContextActions()

  const userSpunKey = `${constants.SPINW_USER_SPUN}${cookie.getSpiceId()}`
  const hadSpunOnLoad = useRef(!!localstorage.get(userSpunKey))
  const hasUserSpun = () => !!localstorage.get(userSpunKey)
  const setUserSpun = () => localstorage.set(userSpunKey, 1)

  const POPUP_TYPE = {
    tooltip: 'tooltip',
    win: 'win',
    loss_default: 'loss_default', // loss after spining
    loss_onload: 'loss_onload', // on first load loss state
    free_spin_lost: 'free_spin_lost' // loss after free spin
  }

  useEffect(() => {
    // Not intented to work for Guest
    if (!isAuthenticated()) {
      return
    }

    const fetchData = async () => {
      try {
        const { points, spins } = await api.iqospherePrize.getSpinWheelStatus(
          campaignCode,
          missionStatusId
        )
        // check if free spins flow only on initial load
        setFreeSpinFlow(spins?.free > 0)
        verifyIfCanSpin(points, spins?.daily, null, spins?.free) // if free spins available show free spins left and wheel, else show regular spins and points left with wheel or popups
      } catch (err) {
        popupToast({
          type: ToastContext.types.error,
          subtitle: translations.tk_errorMsgFetchStatus
        })
        setIsSpinEnabled(false)
      }
      setReady(true)
    }
    fetchData()
  }, [])

  const verifyIfCanSpin = async (ptLeft, spLeft, spinResult, freeSpLeft) => {
    const isFreeSpin = freeSpLeft > 0
    if (isFreeSpin) {
      setSpinsLeft(freeSpLeft > 0 ? freeSpLeft : 0)
    } else {
      setSpinsLeft(spLeft > 0 ? spLeft : 0)
    }
    setPointsLeft(ptLeft)
    const hasSpins = isFreeSpin ? freeSpLeft > 0 : spLeft > 0
    const hasPoints = isFreeSpin ? true : ptLeft >= SPIN_COST

    const isWin = spinResult && spinResult.win === true
    const isLoss = spinResult && spinResult.win === false

    setIsSpinEnabled(hasSpins && hasPoints)

    const progress =
      // if no spins or no points check if any missions or actions uncompleted
      (!hasSpins || !hasPoints) &&
      (await api.iqospherePrize.getCampaignProgress(missionStatusId))

    // Flag to display the Ultimate Prize Popup by Drupal
    if (spinResult) {
      setUserSpun()
    }

    // Verify after a spin
    if (spinResult && isWin) {
      setLatestSpinResult(spinResult)
      setShowWinVideo(true)
      setTimeout(() => {
        showWinPopup(spinResult)
      }, 0)
    } else if (spinResult && isLoss && !freeSpinFlow) {
      if (hasSpins && hasPoints) {
        showLosePopup(spLeft, ptLeft)
        return
      } else if (!hasSpins && !hasPoints) {
        showLoseNoSpinsAndNoPointsLeftPopup(spLeft, progress)
        return
      }
      if (!hasSpins) showLoseNoSpinsLeftPopup(spLeft, progress)
      if (!hasPoints) showLoseNoPointsLeftPopup(spLeft, progress)
      return
    } else if (spinResult && isLoss && freeSpinFlow) {
      showLoseFreeSpinPopup(spLeft, ptLeft, spinResult.spins.free)
    }

    // Otherwise, verify status onload popups
    if (!spinResult) {
      if (
        !localstorage.get(
          `${constants.SPIN_TOOLTIP_DISPLAYED}${cookie.getSpiceId()}`
        )
      ) {
        showSpinWheelToolTip(ptLeft, isFreeSpin ? freeSpLeft : spLeft) // show tooltip on load if not displayed before, pass spins left depending on the flow (free spins or regular spins)
        localstorage.set(
          `${constants.SPIN_TOOLTIP_DISPLAYED}${cookie.getSpiceId()}`,
          1
        )
        return
      }
      if (!hasSpins && !hasPoints) {
        showNoSpinsAndNoPointsLeftPopup(progress)
        return
      }
      if (!hasSpins) showNoSpinsLeftPopup(progress)
      if (!hasPoints) showNoPointsLeftPopup(progress)
    }
  }

  // get url from drupal HTML block
  const extractPrimaryCtaHref = html => {
    let href = null
    parse(html, {
      replace(node) {
        if (
          node.name === 'a' &&
          node.attribs?.['x-cta'] === 'primary' &&
          node.attribs?.href
        ) {
          href = node.attribs.href
        }
      }
    })

    return href
  }

  const safeRedirect = path => {
    if (path) return goToRoute({ mainPath: path })
    return null
  }

  // close X button for popup modal
  const getCTAcloseAction = type => {
    const extracted =
      typeof popupContent?.ctaHtml === 'string' && popupContent?.ctaHtml
        ? extractPrimaryCtaHref(popupContent.ctaHtml)
        : null

    // if freeSpinFlow take only spinsLeft into consideration for deciding the close action
    const hasSpinsPoints = freeSpinFlow
      ? (spinsLeft ?? 0) > 0
      : (spinsLeft ?? 0) > 0 && (pointsLeft ?? 0) >= SPIN_COST

    // 1) ONLOAD
    if (type === POPUP_TYPE.loss_onload) {
      // onload + no points/spins extract first CTA
      if (extracted) return safeRedirect(extracted)
      return setPopupContent(null)
    }

    // 2) TOOLTIP
    if (type === POPUP_TYPE.tooltip) {
      IqosphereAnalytics.txLandingPageEvents('Info Page - Close button')
      // tooltip + has spins & points just close
      if (hasSpinsPoints) return setPopupContent(null)

      // tooltip + no spins/points use first CTA (btnHandler) to verifyIfCanSpin and show appropriate popup
      return popupContent?.btnHandler?.()
    }

    // 3) LOSS (after spin)
    if (type === POPUP_TYPE.loss_default) {
      // has spins & points apply primary CTA - close popup and show the wheel
      if (hasSpinsPoints) return setPopupContent(null)
      // else extract from primary CTA
      if (extracted) return safeRedirect(extracted)
      return setPopupContent(null)
    }

    // 4) WIN
    if (type === POPUP_TYPE.win) {
      // Transition out of free spin flow if no free spins remaining
      if (freeSpinFlow && (latestSpinResult?.spins?.free ?? 0) <= 0) {
        setFreeSpinFlow(false)
      }
      // win + has points/spins close popup and show wheel
      if (hasSpinsPoints) return setPopupContent(null)

      // win + no points/spins use first CTA (btnHandler)
      return popupContent?.btnHandler?.()
    }

    // 5) FREE SPIN LOSS
    if (type === POPUP_TYPE.free_spin_lost) {
      return popupContent?.btnHandler?.()
    }

    // fallback
    return extracted ? safeRedirect(extracted) : setPopupContent(null)
  }

  const showSpinWheelToolTip = (ptLeftArg, spLeftArg) => {
    const ptLeft = typeof ptLeftArg === 'number' ? ptLeftArg : pointsLeft
    const spLeft = typeof spLeftArg === 'number' ? spLeftArg : spinsLeft

    setPopupContent({
      type: POPUP_TYPE.tooltip,
      htmlContent: postLoginContents?.tutorial_tooltip,
      btnText: translations.tk_goToWheel,
      btnHandler: () => {
        IqosphereAnalytics.txLandingPageEvents('Info Page – Go to wheel')
        const hasSpinsPoints = freeSpinFlow
          ? (spLeft ?? 0) > 0
          : (spLeft ?? 0) > 0 && (ptLeft ?? 0) >= SPIN_COST
        return hasSpinsPoints
          ? setPopupContent(null)
          : verifyIfCanSpin(ptLeft, spLeft, null, freeSpinFlow ? spLeft : 0) // if no points - SPIN_COST or no spins we need to check which popup to display next
      }
    })
  }

  const isSafariBrowser = () => {
    if (typeof window === 'undefined') return false
    const ua = window.navigator.userAgent
    return /Safari/.test(ua) && !/Chrome|CriOS|Edg|OPR|FxiOS/.test(ua)
  }
  const isSafari = isSafariBrowser()

  const showPrizePopup = result => {
    const { prize } = result
    setPopupContent({
      type: POPUP_TYPE.win,
      contentExtra: parse(postLoginContents?.win_popup, {
        replace: node => {
          if (
            node.type === 'text' &&
            node.data.includes(CONTENT_TOKENS.PrizeReward)
          ) {
            return (
              <Container
                display={Container.displays.flex}
                flexDirection={Container.flexDirections.column}
                alignItems={Container.alignItemsValues.center}
                py="4">
                <Container position={Container.positions.relative}>
                  <LazyImage
                    height="36"
                    src={prize?.prizeImage}
                    alt={prize?.prizeSmallDescription}
                  />
                </Container>
                <TextTitle
                  heading={TextTitle.headings.h3}
                  alignment={TextTitle.alignments.center}
                  additionalClasses="q-text-white">
                  {prize?.prizeName}
                </TextTitle>
              </Container>
            )
          }
        }
      }),
      btnText: translations.tk_claimPrize,
      btnHandler: () => {
        prize?.prizeName &&
          IqosphereAnalytics.txLandingPageEvents(
            `${prize.prizeName}-Spin Wheel Pick Prize`
          )
        goToRoute({
          mainPath: `${
            addressConfirmationUrl || Routes.iqospherePrizeAddressConfirmation
          }?orderId=${prize?.orderId}`
        })
      },
      btnTextExtra: translations.tk_claimLater,
      btnHandlerExtra: () => {
        prize?.prizeName &&
          IqosphereAnalytics.txLandingPageEvents(
            `${prize.prizeName}-Spin Wheel Pick Prize Later`
          )
        const freeSpins = result.spins.free ?? 0
        const hasSpinsPoints =
          (result.spins.daily ?? 0) > 0 && (result.points ?? 0) >= SPIN_COST
        if (freeSpinFlow) {
          if (freeSpins > 0) {
            setPopupContent(null) // if free spins left show free spins wheel
            return
          }
          setFreeSpinFlow(false)
        }
        // if no free spins left return to regular flow and check if enough points/spins for wheel or show popup
        if (hasSpinsPoints) {
          setPopupContent(null)
        } else {
          verifyIfCanSpin(result.points, result.spins.daily, null, freeSpins)
        }
      }
    })
  }

  const showWinPopup = result => {
    const { prize } = result
    setPopupContent({
      type: POPUP_TYPE.win,
      prize,
      contentExtra: (
        <Container
          display={Container.displays.flex}
          flexDirection={Container.flexDirections.column}
          alignItems={Container.alignItemsValues.center}
          additionalClasses="q-text-white">
          <Container fullWidth position={Container.positions.relative}>
            {/* Just show top section of win popup content over the video */}
            <Css
              s={{
                position: 'absolute',
                inset: 0,
                zIndex: '9999',
                display: 'flex',
                justifyContent: 'center',
                pointerEvents: 'none'
              }}>
              <HtmlContent
                content={
                  postLoginContents?.win_popup?.split(
                    CONTENT_TOKENS.PrizeReward
                  )[0]
                }
              />
            </Css>
          </Container>
        </Container>
      )
    })
  }

  const showNoSpinsLeftPopup = progress => {
    setPopupContent({
      type: POPUP_TYPE.loss_onload,
      htmlContent: postLoginContents?.no_spins,
      ctaHtml: getLossCta(progress)
    })
  }

  const showNoPointsLeftPopup = progress => {
    setPopupContent({
      type: POPUP_TYPE.loss_onload,
      htmlContent: postLoginContents?.no_points,
      ctaHtml: getLossCta(progress)
    })
  }

  const showNoSpinsAndNoPointsLeftPopup = progress => {
    setPopupContent({
      type: POPUP_TYPE.loss_onload,
      htmlContent: postLoginContents?.no_spins_no_points,
      ctaHtml: getLossCta(progress)
    })
  }

  const showLoseNoSpinsLeftPopup = (spLeft, progress) => {
    setPopupContent({
      type: POPUP_TYPE.loss_default,
      htmlContent: loosingJourneyContents.no_spins,
      contentExtra: getSpinsLeftIcon(spLeft),
      ctaHtml: getLossCta(progress)
    })
  }

  const showLoseNoPointsLeftPopup = (spLeft, progress) => {
    setPopupContent({
      type: POPUP_TYPE.loss_default,
      htmlContent: loosingJourneyContents.no_points,
      contentExtra: getSpinsLeftIcon(spLeft),
      ctaHtml: getLossCta(progress)
    })
  }

  const showLoseNoSpinsAndNoPointsLeftPopup = (spLeft, progress) => {
    setPopupContent({
      type: POPUP_TYPE.loss_default,
      htmlContent: loosingJourneyContents.no_spins_no_points,
      contentExtra: getSpinsLeftIcon(spLeft),
      ctaHtml: getLossCta(progress)
    })
  }

  const showLosePopup = (spLeft, ptLeft) => {
    setPopupContent({
      type: POPUP_TYPE.loss_default,
      htmlContent: loosingJourneyContents.spins_and_points_left,
      contentExtra: getSpinsLeftIcon(spLeft),
      btnText: translations.tk_spinAgainBtn,
      btnHandler: () => {
        IqosphereAnalytics.txLandingPageEvents('Spin Again')
        setPopupContent(null)
      },
      ctaHtml: getLossCta({}, true)
    })
  }

  const showLoseFreeSpinPopup = (spLeft, ptLeft, spFreeLeft) => {
    setPopupContent({
      type: POPUP_TYPE.free_spin_lost,
      htmlContent: freeSpinContents?.lose_can_still_spin,
      contentExtra: getSpinsLeftIcon(spFreeLeft),
      btnText: translations.tk_spinAgainBtnFreeSpin,
      btnHandler: () => {
        IqosphereAnalytics.txLandingPageEvents('Bonus spin loss - Spin again')
        const hasSpinsPoints = (spLeft ?? 0) > 0 && (ptLeft ?? 0) >= SPIN_COST
        if (spFreeLeft > 0) {
          setPopupContent(null) // if free spins left show free spins wheel
        } else {
          // if no free spins left return to regular flow and check if enough points/spins for wheel or show popup
          setFreeSpinFlow(false)
          if (hasSpinsPoints) {
            setPopupContent(null)
          } else {
            verifyIfCanSpin(ptLeft, spLeft, null, spFreeLeft)
          }
        }
      },
      ctaHtml: lossDefaultCTA
    })
  }

  const getSpinsLeftIcon = spLeft => {
    const imagesMap = freeSpinFlow
      ? Assets.SPINS_IMAGES_FREE_SPIN
      : Assets.SPINS_IMAGES

    const imageSrc = imagesMap?.[spLeft]

    return (
      <Container
        position={Container.positions.relative}
        display={Container.displays.flex}
        justifyContent={Container.justifyContentValues.center}
        alignItems={Container.alignItemsValues.center}
        py="4">
        {imageSrc && (
          <Container>
            <Image src={imageSrc} additionalClasses="q-block" />
          </Container>
        )}
        <Container
          position={Container.positions.absolute}
          top="0"
          right="0"
          bottom="0"
          left="0"
          display={Container.displays.flex}
          justifyContent={Container.justifyContentValues.center}
          alignItems={Container.alignItemsValues.center}
          additionalClasses="q-pointer-events-none">
          <TextBody
            size={TextBody.sizes.l}
            additionalClasses="q-text-white q-font-bold">
            {spLeft}
          </TextBody>
        </Container>
      </Container>
    )
  }

  const getLossCta = (progress, isLossDefaultCTA) => {
    const {
      allMissionsCompleted = false,
      allTxEarnActionPointsCompleted = false
    } = progress || {}
    if (isLossDefaultCTA) {
      // in case of has points & spins and result is loss we always show lossDefaultCTA
      return lossDefaultCTA
    }
    // checking if missions are complete, if yes then check if actions complete, if yes go to fallbackCTA
    return !allMissionsCompleted
      ? missionCTA
      : !allTxEarnActionPointsCompleted
      ? actionCTA
      : fallbackCTA
  }

  const spinWheelHandler = async (spLeft, ptLeft) => {
    IqosphereAnalytics.txLandingPageEvents(
      freeSpinFlow ? 'Bonus Spins Wheel - Swipe to spin' : 'Swipe to spin wheel'
    )
    setIsSpinEnabled(false)

    // Start animation
    startSpin()

    // Deduct points if not freeSpinFlow and spins client side
    // save previous points so rolling numbers can be animated for points
    setPrevPointsLeft(ptLeft || pointsLeft)
    setPointsLeft(
      freeSpinFlow ? ptLeft || pointsLeft : (ptLeft || pointsLeft) - SPIN_COST
    )
    setSpinsLeft((spLeft || spinsLeft) - 1)

    // Callback endpoint
    let spinWheelCB
    let targetSegment = null
    const lossSegments = [1, 3, 5, 7, 9, 11]
    try {
      const res = await api.iqospherePrize.spinWheel(
        campaignCode,
        spinWheelAction,
        missionStatusId,
        freeSpinFlow
      )
      // Infer target segment from win/loss result
      // Wheel has 12 alternating segments; even indices land on turquoise (win), odd on black (loss)
      if (res?.win !== null) {
        // Win segments (turquoise under pointer): 0, 2, 4, 6, 8, 10 (even)
        // Loss segments (black under pointer): 1, 3, 5, 7, 9, 11 (odd)
        const winSegments = [0, 2, 4, 6, 8, 10]
        const segments = res.win ? winSegments : lossSegments
        targetSegment = segments[Math.floor(Math.random() * segments.length)]
      }
      spinWheelCB = () => {
        verifyIfCanSpin(
          res?.points,
          res?.spins?.daily,
          res,
          freeSpinFlow ? res?.spins?.free : 0
        )
      }
    } catch (err) {
      // Avoid a false visual win on API failure by forcing a loss segment.
      targetSegment =
        lossSegments[Math.floor(Math.random() * lossSegments.length)]
      spinWheelCB = () => {
        popupToast({
          type: ToastContext.types.error,
          subtitle: translations.tk_errorMsgSpinWheel
        })
        setIsSpinEnabled(false)
      }
    }

    stopSpin({ targetSegment, onComplete: spinWheelCB })
  }

  const handleCloseWheel = () => {
    IqosphereAnalytics.txLandingPageEvents(
      freeSpinFlow ? 'Bonus Spins Wheel - Close CTA' : 'Spin wheel close button'
    )
    if (!hasUserSpun() || hadSpunOnLoad.current) {
      // No spin done, or key already existed before this session
      goToRoute({ mainPath: closeRedirectUrl })
    } else {
      // First spin happened this session -> dispatch event, Drupal will close the popup and the spin page
      window.dispatchEvent(new window.CustomEvent('ultimatePrizePopup:show'))
    }
  }

  const replacePlaceholders = html => {
    return html
      .replaceAll(CONTENT_TOKENS.Points, pointsLeft)
      .replaceAll(CONTENT_TOKENS.PointsCost, SPIN_COST)
      .replaceAll(CONTENT_TOKENS.Spins, DAILY_SPINS - spinsLeft)
      .replaceAll(CONTENT_TOKENS.SpinsLeft, spinsLeft)
      .replaceAll(CONTENT_TOKENS.MaxSpins, DAILY_SPINS)
  }

  const isMob = isMobile().any
  const index = isMob ? 1 : 0

  const bg = postLoginContents?.bg_images?.[index] ?? ''

  const bgFreeSpin = freeSpinContents?.bg_images?.[index] ?? ''

  const chosenBg = freeSpinFlow ? bgFreeSpin : bg

  return (
    <Css
      s={{
        background: `url(${chosenBg}) center / cover no-repeat`,
        minHeight: '100svh',
        overflow: 'hidden'
      }}>
      {!ready ? (
        <BlockingLoader darkMode fullScreen loading />
      ) : (
        <>
          <Container p="4">
            <ListContainer columns={3}>
              {/** User points */}
              <Container
                display={Container.displays.flex}
                flexDirection={Container.flexDirections.row}>
                <Image src={Assets.urls.iconPoints} width="6" height="6" />
                <Countdown from={prevPointsLeft} to={pointsLeft} ms={200} />
              </Container>

              {/** Info icon */}
              <Container
                display={Container.displays.flex}
                justifyContent={Container.justifyContentValues.center}>
                <Container
                  onClickHandler={() => {
                    IqosphereAnalytics.txLandingPageEvents(
                      'Spin wheel info page'
                    )
                    showSpinWheelToolTip()
                  }}>
                  <TextBody darkMode>
                    <Icon size={Icon.sizes.s} image={Icon.images.info} />
                  </TextBody>
                </Container>
              </Container>

              {/** Close icon */}
              <Container
                display={Container.displays.flex}
                justifyContent={Container.justifyContentValues.end}>
                {closeRedirectUrl && (
                  <Container onClickHandler={handleCloseWheel}>
                    <TextBody darkMode>
                      <Icon size={Icon.sizes.s} image={Icon.images.cancel} />
                    </TextBody>
                  </Container>
                )}
              </Container>
            </ListContainer>

            {/** Spin the Wheel */}
            <Container layout={Container.layouts.xSmall}>
              <Container
                pt="8"
                display={Container.displays.flex}
                flexDirection={Container.flexDirections.column}
                alignItems={Container.alignItemsValues.center}>
                <HtmlContent
                  content={
                    freeSpinFlow
                      ? freeSpinContents?.content
                      : postLoginContents?.content
                  }
                />
                <Countdown to={spinsLeft} isSizeXL={true} />
              </Container>
              <Container pt="8">
                <Wheel
                  wheelRef={wheelRef}
                  wheelRef2={wheelRef2}
                  pointerRef={pointerRef}
                  showRays={freeSpinFlow}
                />
              </Container>
              <Container
                display={Container.displays.flex}
                justifyContent={Container.justifyContentValues.center}
                pt="6">
                <SlideButton
                  width={260}
                  text={
                    freeSpinFlow
                      ? translations.tk_spinBtnFreeSpin
                      : translations.tk_spinBtn
                  }
                  textUnlocked={
                    freeSpinFlow
                      ? translations.tk_textUnlockedFreeSpin
                      : translations.tk_textUnlocked
                  }
                  disabled={!isSpinEnabled}
                  pillLabel={
                    freeSpinFlow
                      ? translations.tk_pillTextFreeSpin
                      : translations.tk_pillText
                  }
                  showPillIcon={!freeSpinFlow}
                  onSuccess={() => {
                    spinWheelHandler()
                  }}
                />
              </Container>
            </Container>
          </Container>

          {/** Popup contents */}
          <WheelPopupOverlayModal
            isOpen={!!popupContent}
            onClose={() => getCTAcloseAction(popupContent?.type)} // get the correct action for closing the popup
            type={popupContent?.type}
            pointsLeft={pointsLeft}
            POPUP_TYPE={POPUP_TYPE}
            mainBg={bg} // passed in initial props
            freeSpinBG={bgFreeSpin}
            showSpinWheelToolTip={showSpinWheelToolTip}>
            {/* FULLSCREEN WIN VIDEO OVERLAY */}
            {showWinVideo && (
              <WinVideo
                src={
                  isSafari
                    ? postLoginContents?.win_animation_mp4
                    : postLoginContents?.win_animation_webm
                }
                type={isSafari ? 'video/mp4' : 'video/webm'}
                onComplete={() => {
                  setShowWinVideo(false)
                  showPrizePopup(latestSpinResult)
                }}
              />
            )}
            <Container
              fullWidth
              display={Container.displays.flex}
              flexDirection={Container.flexDirections.column}
              justifyContent={Container.justifyContentValues.between}>
              <Container fullWidth>
                {!!popupContent && popupContent?.htmlContent && (
                  <HtmlContent
                    content={replacePlaceholders(popupContent?.htmlContent)}
                  />
                )}
              </Container>
              <Container pt={['2', '5']} fullWidth>
                {!!popupContent && popupContent?.contentExtra}
              </Container>
              <Container
                fullWidth
                display={Container.displays.flex}
                flexDirection={Container.flexDirections.column}
                alignItems={[null, Container.alignItemsValues.center]}>
                {!!popupContent && (
                  <Container
                    width={[
                      'full',
                      popupContent?.type === POPUP_TYPE.win ? 'auto' : '1/5'
                    ]}
                    pt="6">
                    {popupContent?.btnText && popupContent?.btnHandler && (
                      <Container px="0" pt="4">
                        <Button
                          darkMode={true}
                          fullWidth={true}
                          text={popupContent?.btnText}
                          onClickHandler={popupContent?.btnHandler}
                        />
                      </Container>
                    )}
                    {popupContent?.btnTextExtra &&
                      popupContent?.btnHandlerExtra && (
                        <Container
                          display={Container.displays.flex}
                          alignItems={Container.alignItemsValues.center}
                          justifyContent={Container.justifyContentValues.center}
                          fullWidth
                          pt="4">
                          <Container divider={Container.dividers.bottom}>
                            <Button
                              {...(popupContent?.btnText &&
                                popupContent?.btnHandler && {
                                  variant: Button.variants.tertiary
                                })}
                              darkMode={true}
                              text={popupContent?.btnTextExtra}
                              onClickHandler={popupContent?.btnHandlerExtra}
                            />
                          </Container>
                        </Container>
                      )}
                    {popupContent?.ctaHtml && (
                      <Container
                        fullWidth
                        pt="4"
                        onClickHandler={() => {
                          if (popupContent.type === POPUP_TYPE.loss_default) {
                            IqosphereAnalytics.txLandingPageEvents(
                              'Earn Points'
                            )
                          }
                        }}>
                        <HtmlContent content={popupContent.ctaHtml} />
                      </Container>
                    )}
                  </Container>
                )}
              </Container>
            </Container>
          </WheelPopupOverlayModal>
        </>
      )}
    </Css>
  )
}

const SpinWheel = props => {
  return (
    <ToastContext>
      <PublishedComponent
        innerComponent={SpinWheelComponent}
        fullWidth
        {...ComponentDefaultProps}
        {...props}
      />
    </ToastContext>
  )
}

const ComponentTranslations = {
  ...SpinWheelTranslations
}

const ComponentDefaultProps = {
  componentName: 'SpinWheel',
  translations: {
    ...parseTranslations(ComponentTranslations, true)
  }
}

SpinWheel.getInitialProps = async req => {
  return PublishedComponent.getGlobalInitialProps({
    context: req && req.query && req.query.context,
    componentName: ComponentDefaultProps.componentName,
    defaultTranslations: ComponentDefaultProps.translations,
    translationsConfig: ComponentTranslations,
    getSpecificInitialProps: async () => {
      const bodyExtra =
        req?.query?.mock === 'true'
          ? getInitialPropsMock('spinWheel')
          : req?.body?.extra || ''

      return {
        addressConfirmationUrl: bodyExtra?.address_confirmation_url || '',
        campaignCode: bodyExtra?.campaign_wave_code || '',
        closeRedirectUrl: bodyExtra?.fullscreen?.close_redirect_url || '',
        loosingJourneyContents: bodyExtra?.html?.loosing_journey || {},
        mission: bodyExtra?.mission || {},
        missionCTA: bodyExtra?.html?.loosing_journey_cta?.mission_cta || {},
        actionCTA:
          bodyExtra?.html?.loosing_journey_cta?.earn_actions_points_cta || {},
        lossDefaultCTA:
          bodyExtra?.html?.loosing_journey_cta?.lose_spins_and_points_left ||
          {},
        fallbackCTA: bodyExtra?.html?.loosing_journey_cta?.fallback_cta || {},
        postLoginContents: bodyExtra?.post_login || {},
        spinWheelAction: bodyExtra?.activity_id || '',
        freeSpinContents: bodyExtra?.html?.free_spin || {},
        missionStatusId: bodyExtra?.missionStatusId || ''
      }
    }
  })
}

export default SpinWheel
